// New Year Animation Component
// Animación de Año Nuevo que se ejecuta una vez al cargar

import { Box, Button, keyframes } from "@mui/material";
import { memo, useState, useEffect } from "react";

// ============================================================================
// KEYFRAMES ANIMATIONS
// ============================================================================

const vem2017 = keyframes`
  0% { right: 0%; }
  66.6666% { right: 50%; margin-right: -300px; }
  90% { right: 50%; margin-right: -300px; }
  100% { right: 50%; }
`;

const vemEVaiBalao = keyframes`
  0% { right: 0%; }
  40% { right: 50%; margin-right: -300px; }
  50% { right: 50%; margin-right: -200px; top: 50%; }
  100% { top: -100%; right: 50%; }
`;

const entregaBalao = keyframes`
  0% { transform: rotate(-30deg); width: 40px; }
  100% { transform: rotate(-150deg); width: 70px; }
`;

const vai2016 = keyframes`
  0% { top: 50%; }
  100% { top: -100%; }
`;

const vemFeliz = keyframes`
  0% { margin-top: 0px; opacity: 0; }
  100% { margin-top: -200px; opacity: 1; }
`;

const soltaFogos = keyframes`
  0% { margin-top: 100%; opacity: 0; width: 2px; height: 30px; border-radius: 50%; }
  75% { margin-top: 0%; opacity: 1; width: 2px; height: 30px; border-radius: 50%; }
  80% { margin-top: 0px; margin-left: 0px; opacity: 1; width: 10px; height: 10px; border-radius: 50%; transform: scale(0.2); }
  100% { opacity: 1; width: 10px; height: 10px; border-radius: 50%; transform: scale(1); }
`;

const estouraFogos = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const fadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; visibility: hidden; }
`;

const pulseButton = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// ============================================================================
// COMPONENT
// ============================================================================

interface NewYearAnimationProps {
  onAnimationEnd?: () => void;
}

const NewYearAnimation = memo(function NewYearAnimation({ onAnimationEnd }: NewYearAnimationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Precargar fuentes antes de iniciar la animación
  useEffect(() => {
    // Agregar link de precarga de fuentes al head
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@700&family=Pacifico&family=Dancing+Script:wght@700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Esperar a que las fuentes estén cargadas
    const checkFonts = async () => {
      try {
        await document.fonts.ready;
        // Pequeño delay adicional para asegurar que todo esté renderizado
        setTimeout(() => setIsReady(true), 300);
      } catch {
        // Si falla, iniciar de todos modos después de un timeout
        setTimeout(() => setIsReady(true), 1000);
      }
    };

    checkFonts();

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Auto-hide after animation completes (approx 12 seconds after ready)
  useEffect(() => {
    if (!isReady) return;

    const timer = setTimeout(() => {
      handleSkip();
    }, 12000);

    return () => clearTimeout(timer);
  }, [isReady]);

  const handleSkip = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      onAnimationEnd?.();
    }, 500);
  };

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 9999,
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        animation: isFadingOut ? `${fadeOut} 0.5s ease-out forwards` : "none",
      }}
    >
      {/* Loading mientras cargan las fuentes */}
      {!isReady && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              border: "4px solid rgba(255,255,255,0.2)",
              borderTop: "4px solid #ffd700",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Box sx={{ color: "rgba(255,255,255,0.7)", fontSize: "14px" }}>
            Cargando...
          </Box>
        </Box>
      )}

      {/* Mensaje Feliz Año Nuevo */}
      {isReady && (
        <Box
          className="feliz"
          sx={{
            width: "100%",
            fontFamily: "'Pacifico', 'Dancing Script', cursive",
            fontSize: { xs: "32px", sm: "50px", md: "70px", lg: "90px" },
            fontWeight: 700,
            background: "linear-gradient(90deg, #ff6b9d, #c44569, #f8b500, #ff6b9d)",
            backgroundSize: "300% 300%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textAlign: "center",
            position: "absolute",
            top: "50%",
            opacity: 0,
            textShadow: "0 0 30px rgba(255, 107, 157, 0.5)",
            animation: `${vemFeliz} 2s ease-in-out 7s forwards`,
            px: 2,
          }}
        >
          Feliz Año Nuevo Familia Fibramax
        </Box>
      )}

      {/* Año Nuevo Container */}
      {isReady && (
        <Box
          className="ano_novo"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            padding: { xs: "150px 50px 0px 0px", md: "200px 100px 0px 0px" },
          }}
        >
        {/* 202 */}
        <Box
          component="span"
          sx={{
            fontFamily: "'Montserrat', 'Roboto', sans-serif",
            fontSize: { xs: "80px", sm: "120px", md: "175px" },
            fontWeight: 700,
            background: "linear-gradient(180deg, #7a8fe8 0%, #5a6fd6 50%, #8b9df0 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textShadow: "0 0 40px rgba(122, 143, 232, 0.4)",
            letterSpacing: "-5px",
          }}
        >
          202
        </Box>

        {/* 5 que se va */}
        <Box
          component="span"
          className="seis"
          sx={{
            fontFamily: "'Montserrat', 'Roboto', sans-serif",
            fontSize: { xs: "80px", sm: "120px", md: "175px" },
            fontWeight: 700,
            background: "linear-gradient(180deg, #7a8fe8 0%, #5a6fd6 50%, #8b9df0 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            position: "absolute",
            top: "50%",
            right: "50%",
            marginRight: { xs: "-100px", sm: "-150px", md: "-200px" },
            animation: `${vai2016} 5s ease-in-out 5s forwards`,
          }}
        >
          5
        </Box>

        {/* 6 que viene */}
        <Box
          component="span"
          className="sete"
          sx={{
            fontFamily: "'Montserrat', 'Roboto', sans-serif",
            fontSize: { xs: "80px", sm: "120px", md: "175px" },
            fontWeight: 700,
            background: "linear-gradient(180deg, #f8b500 0%, #ffd700 50%, #ffec8b 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textShadow: "0 0 50px rgba(248, 181, 0, 0.6)",
            position: "absolute",
            right: "0%",
            marginRight: { xs: "-100px", sm: "-150px", md: "-200px" },
            animation: `${vem2017} 6s ease-in-out forwards`,
            "&::before": {
              content: '""',
              width: "0px",
              height: "6px",
              display: "block",
              borderRadius: "3px",
              background: "linear-gradient(90deg, #7a8fe8, #ffd700)",
              transform: "rotate(0deg)",
              transformOrigin: "left top",
              position: "absolute",
              top: { xs: "30px", sm: "40px", md: "55px" },
              left: "10px",
              zIndex: -1,
              animation: `${entregaBalao} 1s ease-in-out 4s`,
            },
          }}
        >
          6
        </Box>

        {/* Globo */}
        <Box
          className="balao"
          sx={{
            width: { xs: "60px", sm: "80px", md: "100px" },
            height: { xs: "60px", sm: "80px", md: "100px" },
            display: "block",
            background: "linear-gradient(135deg, #ffd700 0%, #f8b500 50%, #e8a800 100%)",
            borderRadius: "50%",
            position: "absolute",
            top: "50%",
            marginTop: { xs: "-120px", sm: "-140px", md: "-165px" },
            right: "0%",
            marginRight: { xs: "-100px", sm: "-150px", md: "-200px" },
            animation: `${vemEVaiBalao} 10s ease-in-out forwards`,
            boxShadow: "0 0 30px rgba(255, 215, 0, 0.5), inset 0 -10px 20px rgba(0,0,0,0.1)",
            "&::before": {
              content: '""',
              width: 0,
              height: 0,
              borderStyle: "solid",
              borderWidth: "0 10px 20px 10px",
              borderColor: "transparent transparent #b19b32 transparent",
              position: "absolute",
              left: "50%",
              marginLeft: "-10px",
              bottom: "-10px",
              zIndex: -1,
            },
            "&::after": {
              content: '""',
              width: "4px",
              height: { xs: "60px", sm: "80px", md: "100px" },
              display: "block",
              background: "linear-gradient(180deg, #fff 0%, #ccc 100%)",
              borderRadius: "0px 0px 3px 3px",
              position: "absolute",
              left: "50%",
              marginLeft: "-2px",
              bottom: { xs: "-70px", sm: "-90px", md: "-110px" },
            },
          }}
        />
        </Box>
      )}

      {/* Fogos (Fuegos CSS) */}
      {isReady && (
        <Box
          className="fogos"
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            position: "absolute",
            top: 0,
            left: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
        {[
          { className: "f1", left: "20%", top: "40%" },
          { className: "f2", left: "15%", top: "70%" },
          { className: "f3", right: "20%", top: "40%" },
          { className: "f4", right: "15%", top: "70%" },
        ].map((fogo, index) => (
          <Box
            key={index}
            sx={{
              border: "2px solid #fff",
              position: "absolute",
              opacity: 0,
              animation: `${soltaFogos} 1.5s ease-in-out 8s forwards`,
              ...(fogo.left && { left: fogo.left }),
              ...(fogo.right && { right: fogo.right }),
              top: fogo.top,
            }}
          >
            {[0, 120, 240].map((rotation, spanIndex) => (
              <Box
                key={spanIndex}
                component="span"
                sx={{
                  width: "6px",
                  height: "6px",
                  display: "block",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  transform: `rotate(${rotation}deg)`,
                  animation: `${estouraFogos} 0.5s ease-in-out 9s forwards`,
                  "&::before": {
                    content: '""',
                    width: "2px",
                    height: "50px",
                    display: "block",
                    background: "linear-gradient(180deg, #f5cc06 0%, #ff6b6b 100%)",
                    position: "absolute",
                    top: "-60px",
                    left: "2px",
                    boxShadow: "0 0 10px #f5cc06",
                  },
                  "&::after": {
                    content: '""',
                    width: "2px",
                    height: "50px",
                    display: "block",
                    background: "linear-gradient(180deg, #ff6b6b 0%, #f5cc06 100%)",
                    position: "absolute",
                    bottom: "-60px",
                    left: "2px",
                    boxShadow: "0 0 10px #f5cc06",
                  },
                }}
              >
                <Box
                  component="i"
                  sx={{
                    "&::before": {
                      content: '""',
                      width: "4px",
                      height: "4px",
                      display: "block",
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: "-15px",
                      left: "10px",
                      boxShadow: "0 0 8px #fff",
                    },
                    "&::after": {
                      content: '""',
                      width: "4px",
                      height: "4px",
                      display: "block",
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: "-15px",
                      right: "10px",
                      boxShadow: "0 0 8px #fff",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        ))}
        </Box>
      )}

      {/* Botón para saltar */}
      <Button
        onClick={handleSkip}
        variant="outlined"
        sx={{
          position: "absolute",
          bottom: { xs: 20, md: 30 },
          right: { xs: 20, md: 30 },
          color: "rgba(255, 255, 255, 0.8)",
          borderColor: "rgba(255, 255, 255, 0.4)",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: { xs: "12px", md: "14px" },
          padding: { xs: "8px 16px", md: "10px 24px" },
          borderRadius: "25px",
          textTransform: "none",
          backdropFilter: "blur(10px)",
          background: "rgba(255, 255, 255, 0.1)",
          animation: `${pulseButton} 2s ease-in-out infinite`,
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "rgba(255, 255, 255, 0.8)",
            background: "rgba(255, 255, 255, 0.2)",
            transform: "scale(1.05)",
          },
        }}
      >
        Saltar animación
      </Button>
    </Box>
  );
});

export default NewYearAnimation;
